import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Navbar from "../components/Navbar";
import toast from "react-hot-toast";
import {
  FiArrowLeft,
  FiGift,
  FiAward,
  FiShield,
  FiLink,
  FiPlus,
  FiTrash2,
  FiInfo,
  FiSave,
  FiRefreshCw,
  FiX,
  FiUpload,
  FiImage,
  FiCheck,
  FiChevronLeft,
  FiChevronRight,
  FiArrowUp,
  FiArrowDown,
  FiEye,
  FiEyeOff,
} from "react-icons/fi";
import {
  createEvent,
  editEvent,
  fetchEventDetails,
  fetchEventSponsors,
  addEventSponsor,
  updateEventSponsor,
  deleteEventSponsor,
  fetchEventPrizes,
  createEventPrize,
  updateEventPrize,
  deleteEventPrize,
  uploadEventCardImage,
} from "../../utils/eventApi";
import {
  emptyTheme,
  themeFromApi,
  themePayload,
  rulesFromApi,
  rulesPayload,
  themeProblem,
  cardImageFileError,
  defaultRules,
  defaultRuleRows,
} from "../../utils/eventTheme";
import {
  rewardsOf,
  buildRewards,
  totalRewards,
  normaliseCurrency,
  isAutoCredited,
  EMPTY_PRIZE_TEXT,
  COIN_CURRENCIES,
} from "../../utils/eventRewards";
import {
  LOGO_ACCEPT,
  LOGO_HINT,
  logoFileError,
  iconUrlChanged,
  imageFromClipboard,
} from "../../utils/sponsorLogo";
import CurrencySelect from "./components/CurrencySelect";
import UtcClock from "./components/UtcClock";
import EventThemeStep from "./components/EventThemeStep";

/**
 * Dates in this form are UTC wall-clock strings.
 *
 * The fields are native `datetime-local` inputs, which carry no timezone at
 * all — the browser hands back exactly what was typed. Reading that as the
 * local zone is what made an admin typing "00:00" set 18:30 UTC the day before.
 * It is read as UTC instead, end to end: what is typed is what the API stores.
 *
 * `YYYY-MM-DDTHH:mm` is both the input value format and what these produce, so
 * the string held in state is the string in the box.
 */

/** An API instant to what the UTC-interpreted input should show. */
const toUtcInputValue = (isoString) => {
  if (!isoString) return "";
  const d = new Date(isoString);
  if (isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 16);
};

/**
 * One input value as a Date, read as UTC.
 *
 * Seconds are dropped before the `Z` is appended: some browsers include them
 * when `step` allows, and "…T10:00:30" + ":00Z" is not a date at all.
 */
const UTC_INPUT_SHAPE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;

const utcInstant = (value) => {
  const trimmed = String(value || "").slice(0, 16);

  // The shape is checked before parsing because the Date parser is lenient
  // enough to turn "not-a-date:00Z" into 1 Jan 2000 rather than rejecting it.
  // A native input cannot produce that, but a silent year-2000 event date is
  // not a failure mode worth leaving open.
  if (!UTC_INPUT_SHAPE.test(trimmed)) return null;

  const d = new Date(`${trimmed}:00Z`);
  return isNaN(d.getTime()) ? null : d;
};

/** What the input holds, as the instant to send. */
const fromUtcInputValue = (value) => {
  const d = utcInstant(value);
  return d ? d.toISOString() : "";
};

/** Compare two input values as instants — used by the schedule guard. */
const utcMs = (value) => {
  const d = utcInstant(value);
  return d ? d.getTime() : NaN;
};

/**
 * Add whole days, in UTC.
 *
 * `setUTCDate` rather than string arithmetic, so month ends and leap days are
 * handled by the platform — and staying in UTC means no daylight-saving shift
 * can move the clock time the admin chose.
 */
const addUtcDays = (value, days) => {
  const d = utcInstant(value);
  if (!d) return "";
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 16);
};

/** The same instant on the reader's own clock, for the hint under each field. */
const localHintFor = (value) => {
  const d = utcInstant(value);
  if (!d) return "";
  return d
    .toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    })
    .replace(",", "");
};

// ── the reward rows inside one tier ────────────────────────────────────────
//
// A tier's prize is the `rewards` list. PX is edited as its own field because
// it is the one currency finalize credits by itself; every other currency is a
// row under it. Amounts stay as typed — `buildRewards` drops the blank and
// half-typed ones when the tier is sent.

const pxAmountOf = (rewards) => {
  const row = (rewards || []).find((entry) => normaliseCurrency(entry.currency) === "PX");
  return row ? row.amount : "";
};

const coinRowsOf = (rewards) =>
  (rewards || []).filter((entry) => normaliseCurrency(entry.currency) !== "PX");

const composeRewards = (pxAmount, coinRows) => [
  ...(String(pxAmount ?? "").trim() === "" ? [] : [{ currency: "PX", amount: pxAmount }]),
  ...coinRows,
];

/**
 * The form, one step at a time.
 *
 * Six sections down one page meant an event's dates and its prize ladder were
 * the same act of scrolling, and nothing said how much was left. Each step is a
 * section that already existed; the order is the order they depend on each
 * other — dates before the prizes those dates pay out.
 *
 * `validate` is what must be true to move past the step. Steps without one are
 * genuinely optional: an event with no sponsors is a real event.
 */
const STEPS = [
  {
    key: "schedule",
    label: "Schedule",
    hint: "When the event runs",
    validate: ({ formData }) => {
      if (!formData.start_date || !formData.end_date) {
        return "Please provide both start and end dates";
      }
      if (utcMs(formData.end_date) <= utcMs(formData.start_date)) {
        return "End date must be after start date";
      }
      return null;
    },
  },
  {
    key: "basics",
    label: "Basics",
    hint: "Name and code",
    validate: ({ formData, isEditing }) => {
      if (!isEditing && !formData.event_code?.trim()) return "Please enter event code";
      if (!formData.name?.trim()) return "Please enter event name";
      return null;
    },
  },
  { key: "sponsors", label: "Sponsors", hint: "Partner logos" },
  { key: "prizes", label: "Prizes", hint: "Rank rewards" },
  { key: "rules", label: "Rules", hint: "Points & leaderboard" },
  { key: "links", label: "Links", hint: "Banner and support" },
  {
    key: "design",
    label: "App Design",
    hint: "Live Event card, colors, text and terms",
    validate: ({ formData, theme, hasCardImage }) =>
      themeProblem(theme, { hasImage: hasCardImage, enabled: Boolean(formData.theme_enabled) }),
  },
];

/**
 * The four points at which an event messages users.
 *
 * Separate switches because they are separate decisions: a quiet internal event
 * may still want its winners told, and one announced elsewhere may want nothing
 * at launch but everything at the end.
 *
 * `sends` names who actually receives it — "notify users" is a different number
 * of people at each moment, and that is the part worth knowing before turning
 * one off.
 */
const NOTIFY_MOMENTS = [
  {
    field: "notify_on_create",
    label: "Event announced",
    when: "Sent when you publish it",
    sends: "everyone",
  },
  {
    field: "notify_on_running",
    label: "Event opens",
    when: "Sent when it actually goes live",
    sends: "everyone",
  },
  {
    field: "notify_on_close",
    label: "Event closes",
    when: "Sent when scoring ends",
    sends: "everyone",
  },
  {
    field: "notify_on_winner",
    label: "Winners told",
    when: "Sent at finalize, naming the prize",
    sends: "winners only",
  },
];

/**
 * How each activity converts into event points, one field per source.
 *
 * `unit` is one of the left-hand side — "PX mined", "member joins" — so every
 * field reads as "1 <unit> = N pts". PX-based rates default to 1 (one PX, one
 * point); per-action awards default to 0, which means off.
 */
const POINT_RATES = [
  { field: "mining_points_per_px", label: "Daily Mining", unit: "PX mined", fallback: 1 },
  { field: "streak_points_per_px", label: "Streak Bonus", unit: "PX of streak bonus", fallback: 1 },
  { field: "referral_points_per_px", label: "Referral Mining Bonus", unit: "PX of referral bonus", fallback: 1 },
  { field: "referral_join_points", label: "Referral Join", unit: "member joins", fallback: 0 },
  { field: "creator_content_points", label: "Creator Hub — Content Approved", unit: "content approved", fallback: 0 },
  { field: "creator_bonus_points_per_px", label: "Creator Hub — Bonus Claim", unit: "PX bonus claimed", fallback: 1 },
];

/**
 * The standard "Eligible Point Activities" list a new event starts with.
 *
 * Built from the form's own rates rather than written out as fixed copy, so
 * an event that pays 2 points per PX mined does not tell users it pays 1.
 * A blank rate field means its default, the same as on save.
 */
const defaultEligibility = (formData) => {
  const rate = (field, fallback) => {
    const value = formData[field];
    return value === "" || value == null || isNaN(Number(value)) ? fallback : Number(value);
  };
  const pts = (n) => `${n} Event ${n === 1 ? "Point" : "Points"}`;

  return [
    {
      title: "Daily Check-in",
      description: `Gives exactly +${pts(rate("daily_check_in_reward", 1))} daily`,
    },
    { title: "Daily Mining", description: `1 PX mined = ${pts(rate("mining_points_per_px", 1))}` },
    {
      title: "Referral Activity",
      description: `1 PX from referrals = ${pts(rate("referral_points_per_px", 1))}`,
    },
    { title: "Streak Bonus", description: `1 PX from streaks = ${pts(rate("streak_points_per_px", 1))}` },
    {
      title: "Creator Hub",
      description: `1 PX from Creator Hub = ${pts(rate("creator_bonus_points_per_px", 1))}`,
    },
    { title: "Mini Games", description: "Play mini games and get Event Points" },
  ].map((row, i) => ({ ...row, is_active: true, _localId: `default-${i}` }));
};

const initialFormData = {
  event_code: "",
  name: "",
  description: "",
  start_date: "",
  end_date: "",
  daily_check_in_reward: "",
  check_in_credits_coin: false,
  ...Object.fromEntries(POINT_RATES.map((r) => [r.field, ""])),
  notify_on_create: true,
  notify_on_running: true,
  notify_on_close: true,
  notify_on_winner: true,
  prize_top_n: "",
  banner_url: "",
  prize_info_text: "",
  support_url: "",
  sponsor_cta_url: "",
  // Off until an admin turns the App Design on — the app keeps its standard design.
  theme_enabled: false,
  leaderboard_limit: "",
  rank_bucket_size: "",
};

const EventForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  const [formData, setFormData] = useState(initialFormData);
  const [sponsors, setSponsors] = useState([]);
  const [tiers, setTiers] = useState([]);
  // The Eligibility section: title + description rows, saved in this order.
  // A new event starts with the standard list; an edit loads what was saved.
  const [eligibility, setEligibility] = useState(() =>
    isEditing ? [] : defaultEligibility(initialFormData)
  );
  /**
   * Whether the list is still the generated default.
   *
   * While it is, it follows the rates — set mining to 2 on the Rules step and
   * the Daily Mining row says so. The first hand edit takes the list over and
   * it is never regenerated behind the admin's back after that.
   */
  const [eligibilityIsDefault, setEligibilityIsDefault] = useState(!isEditing);
  // The App Design step. Blank fields mean the app's own default.
  const [theme, setTheme] = useState(emptyTheme);
  // A new event starts with the standard rules written in, ready to edit; an
  // existing one shows exactly what it saved.
  const [rules, setRules] = useState(() => (isEditing ? [] : defaultRuleRows()));
  // A picked card image waits here until the event is saved, then uploads.
  const [cardImageFile, setCardImageFile] = useState(null);
  const [cardImagePreview, setCardImagePreview] = useState("");
  const hasCardImage = Boolean(cardImageFile) || Boolean(String(theme.card_bg_image || "").trim());
  const [deletedSponsorIds, setDeletedSponsorIds] = useState([]);
  const [deletedPrizeIds, setDeletedPrizeIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEditing);
  const [eventStatus, setEventStatus] = useState("draft");

  const isRunning = eventStatus === "running";
  // Announced but not open: fully editable, exactly like a draft. Only the
  // running state locks fields, so this is deliberately not lumped in with it.
  const isComingSoon = eventStatus === "coming_soon";
  const isCompleted = eventStatus === "completed";

  const [step, setStep] = useState(0);
  const isLastStep = step === STEPS.length - 1;

  /**
   * The dates exactly as they were loaded, so a save can tell an edit from an
   * unchanged field.
   *
   * The form edits to the minute, but the API stores seconds, so reading a date
   * in and writing it straight back out loses up to 59 seconds. For a running
   * event that reads as moving the end date *earlier*, which the API refuses —
   * so opening the form and pressing Save was enough to be rejected. Comparing
   * against what was loaded means an untouched date is simply not sent.
   */
  const [loadedDates, setLoadedDates] = useState({ start: "", end: "" });

  /**
   * When the announcement went out, if it has.
   *
   * The API stamps this once and will not send a second time, so the checkbox
   * says so rather than implying a re-publish would notify again.
   */
  const [loadedNotifyStamps, setLoadedNotifyStamps] = useState({});

  /**
   * Schedule shortcuts.
   *
   * Both write the form's own local `YYYY-MM-DDTHH:mm` string, seconds cleared,
   * so they go through exactly the same path as a hand-picked date — including
   * the "only send a date that changed" rule on save.
   */

  /** The current minute, as the form stores dates. */
  const nowForForm = () => toUtcInputValue(new Date().toISOString());

  const handleStartNow = () => {
    const start = nowForForm();
    setFormData((prev) => {
      // An end date already before the new start would be invalid the moment it
      // is set, so it is carried forward rather than left to fail validation.
      const endStillValid = prev.end_date && utcMs(prev.end_date) > utcMs(start);
      return {
        ...prev,
        start_date: start,
        end_date: endStillValid ? prev.end_date : addUtcDays(start, 7),
      };
    });
    toast.success("Start set to the current UTC minute");
  };

  const handleEndAfterDays = (days) => {
    setFormData((prev) => {
      // Relative to the start, or to now when there is no start yet — "+7 days"
      // has to be 7 days from something.
      const start = prev.start_date || nowForForm();
      return { ...prev, start_date: start, end_date: addUtcDays(start, days) };
    });
    toast.success(`End set to ${days} days after the start`);
  };

  /** What is stopping this step from being left, or null. */
  const stepProblem = (index) =>
    STEPS[index].validate ? STEPS[index].validate({ formData, isEditing, theme, hasCardImage }) : null;

  /**
   * Move to a step.
   *
   * Going forward has to clear every step in between, so skipping ahead cannot
   * quietly leave the dates blank. Going back never validates — you are allowed
   * to walk away from a half-filled step to check something earlier.
   */
  const goToStep = (target) => {
    if (target > step) {
      for (let i = step; i < target; i++) {
        const problem = stepProblem(i);
        if (problem) {
          setStep(i);
          toast.error(problem);
          return;
        }
      }
    }
    setStep(Math.max(0, Math.min(STEPS.length - 1, target)));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Steps cleared so far, for the bar. An editable event arrives already valid,
  // so editing does not open on a bar that reads 0%.
  const clearedCount = STEPS.filter((_, i) => !stepProblem(i)).length;
  const progressPct = Math.round((clearedCount / STEPS.length) * 100);

  // Load existing event data if editing
  useEffect(() => {
    if (!isEditing) return;

    const loadData = async () => {
      setFetching(true);
      try {
        const [eventRes, sponsorsRes, prizesRes] = await Promise.allSettled([
          fetchEventDetails(id),
          fetchEventSponsors(id),
          fetchEventPrizes(id),
        ]);

        if (eventRes.status === "fulfilled" && eventRes.value?.status) {
          const ev = eventRes.value.data;
          setEventStatus(ev.status || "draft");
          setFormData({
            event_code: ev.event_code || "",
            name: ev.name || "",
            description: ev.description || "",
            start_date: toUtcInputValue(ev.start_date),
            end_date: toUtcInputValue(ev.end_date),
            daily_check_in_reward: ev.daily_check_in_reward ?? "",
            check_in_credits_coin: Boolean(ev.check_in_credits_coin),
            ...Object.fromEntries(POINT_RATES.map((r) => [r.field, ev[r.field] ?? ""])),
            // Default on for an event saved before the flag existed.
            // Default on for an event saved before these flags existed.
            notify_on_create: ev.notify_on_create !== false,
            notify_on_running: ev.notify_on_running !== false,
            notify_on_close: ev.notify_on_close !== false,
            notify_on_winner: ev.notify_on_winner !== false,
            prize_top_n: ev.prize_top_n ?? "",
            banner_url: ev.banner_url || "",
            prize_info_text: ev.prize_info_text || "",
            support_url: ev.support_url || "",
            sponsor_cta_url: ev.sponsor_cta_url || "",
            theme_enabled: ev.theme_enabled === true,
            leaderboard_limit: ev.leaderboard_limit ?? "",
            rank_bucket_size: ev.rank_bucket_size ?? "",
          });
          setEligibility(
            (ev.eligibility || []).map((row, i) => ({
              _localId: `loaded-${i}`,
              title: row.title || "",
              description: row.description || "",
              is_active: row.is_active !== false,
            }))
          );
          // Nothing saved: the API sent the default list, which then keeps
          // following the rates here exactly as it does in the app.
          setEligibilityIsDefault(Boolean(ev.eligibility_is_default));
          setTheme(themeFromApi(ev.theme));
          setRules(rulesFromApi(ev.rules));
          setLoadedDates({
            start: toUtcInputValue(ev.start_date),
            end: toUtcInputValue(ev.end_date),
          });
          setLoadedNotifyStamps({
            notify_on_create: ev.notified_at || null,
            notify_on_running: ev.live_notified_at || null,
            notify_on_close: ev.completed_notified_at || null,
            // Winners are messaged per person at finalize, so there is no single
            // stamp for it — finalize itself is the record.
            notify_on_winner: null,
          });
        }

        if (sponsorsRes.status === "fulfilled" && sponsorsRes.value?.status) {
          setSponsors(
            (sponsorsRes.value.data || []).map((s) => ({
              id: s.id,
              name: s.name || "",
              // The API sends the same link under both names.
              website_url: s.website_url || s.url || "",
              icon_url: s.icon_url || "",
              // Kept so the save can tell an edited URL from one just echoed
              // back — see `iconUrlChanged`.
              icon_url_original: s.icon_url || "",
              logo_file: null,
              logo_preview: s.icon_url || "",
              sort_order: s.sort_order ?? 0,
              is_active: s.is_active ?? true,
            }))
          );
        }

        if (prizesRes.status === "fulfilled" && prizesRes.value?.status) {
          setTiers(
            (prizesRes.value.data || []).map((p) => ({
              id: p.id,
              rank_from: p.rank_from ?? 1,
              rank_to: p.rank_to ?? p.rank_from ?? 1,
              rank_label: p.rank_label || "",
              // Tiers saved before the API had `rewards` arrive with only
              // `reward_px` / `reward_usdt`, which `rewardsOf` reads instead.
              rewards: rewardsOf(p),
              sort_order: p.sort_order ?? 1,
            }))
          );
        }
      } catch (err) {
        toast.error("Failed to load event data");
      } finally {
        setFetching(false);
      }
    };

    loadData();
  }, [id, isEditing]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // ==========================================
  // Eligibility handlers
  // ==========================================
  // Order is the row's position, so moving a row is the whole of "set order".
  // Every hand edit goes through `editEligibility`, which ends the default sync.
  const editEligibility = (update) => {
    setEligibilityIsDefault(false);
    setEligibility(update);
  };

  // Keep an untouched default list in step with the rates it quotes.
  const defaultRateKey = [
    formData.daily_check_in_reward,
    formData.mining_points_per_px,
    formData.referral_points_per_px,
    formData.streak_points_per_px,
    formData.creator_bonus_points_per_px,
  ].join("|");

  useEffect(() => {
    if (eligibilityIsDefault) setEligibility(defaultEligibility(formData));
    // formData is read only for the rates, which defaultRateKey covers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eligibilityIsDefault, defaultRateKey]);

  const handleUseDefaultEligibility = () => {
    const hasContent = eligibility.some((row) => row.title?.trim() || row.description?.trim());
    if (
      hasContent &&
      !eligibilityIsDefault &&
      !window.confirm("Replace the current eligibility list with the default one?")
    ) {
      return;
    }
    setEligibility(defaultEligibility(formData));
    setEligibilityIsDefault(true);
  };

  const handleAddEligibility = () =>
    editEligibility((prev) => [...prev, { _localId: Date.now(), title: "", description: "", is_active: true }]);

  const handleUpdateEligibility = (index, field, value) =>
    editEligibility((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));

  const handleRemoveEligibility = (index) =>
    editEligibility((prev) => prev.filter((_, i) => i !== index));

  const handleMoveEligibility = (index, delta) =>
    editEligibility((prev) => {
      const target = index + delta;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });

  // ==========================================
  // Sponsor handlers
  // ==========================================
  const handleAddSponsor = () => {
    setSponsors((prev) => [
      ...prev,
      {
        _localId: Date.now(),
        name: "",
        website_url: "",
        icon_url: "",
        logo_file: null,
        logo_preview: "",
        sort_order: prev.length + 1,
        is_active: true,
      },
    ]);
  };

  const handleUpdateSponsor = (index, field, value) => {
    setSponsors((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  /**
   * Take a logo file for one sponsor row.
   *
   * Rejected here for the same reasons the API would reject it, so a 5MB photo
   * is caught on selection rather than after the event has already been saved
   * and the upload is the one thing that failed.
   */
  const handleSponsorLogoChange = (index, file, inputEl) => {
    if (!file) return;

    const error = logoFileError(file);
    if (error) {
      toast.error(error);
      if (inputEl) inputEl.value = "";
      return;
    }

    setSponsors((prev) => {
      const updated = [...prev];
      const current = updated[index];
      if (current.logo_preview && current.logo_preview.startsWith("blob:")) {
        URL.revokeObjectURL(current.logo_preview);
      }
      updated[index] = {
        ...current,
        logo_file: file,
        logo_preview: URL.createObjectURL(file),
      };
      return updated;
    });
  };

  /**
   * Paste an image straight into a sponsor row.
   *
   * A logo is usually already on the clipboard — a screenshot, or an image
   * copied from the sponsor's own site — and saving it to disk first only to
   * pick it back up is a detour. Text pastes are left alone, so pasting a URL
   * into the URL box still behaves like a URL box.
   */
  const handleSponsorLogoPaste = (index, event) => {
    const image = imageFromClipboard(event);
    if (!image) return;

    // Only once an image is actually found, so a text paste is untouched.
    event.preventDefault();
    handleSponsorLogoChange(index, image);
    toast.success("Logo pasted from clipboard");
  };

  /** Drop a chosen file and fall back to whatever URL the row holds. */
  const handleClearSponsorLogo = (index) => {
    setSponsors((prev) => {
      const updated = [...prev];
      const current = updated[index];
      if (current.logo_preview && current.logo_preview.startsWith("blob:")) {
        URL.revokeObjectURL(current.logo_preview);
      }
      updated[index] = { ...current, logo_file: null, logo_preview: current.icon_url || "" };
      return updated;
    });
  };

  const handleRemoveSponsor = (index) => {
    setSponsors((prev) => {
      const target = prev[index];
      if (target.id) {
        setDeletedSponsorIds((d) => [...d, target.id]);
      }
      if (target.logo_preview && target.logo_preview.startsWith("blob:")) {
        URL.revokeObjectURL(target.logo_preview);
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  // ==========================================
  // Card image handlers (App Design step)
  // ==========================================
  /** Take a card image. Checked here so a 9MB photo is refused on pick, not on save. */
  const handlePickCardImage = (file, inputEl) => {
    if (!file) return;

    const error = cardImageFileError(file);
    if (error) {
      toast.error(error);
      if (inputEl) inputEl.value = "";
      return;
    }

    if (cardImagePreview.startsWith("blob:")) URL.revokeObjectURL(cardImagePreview);
    setCardImageFile(file);
    setCardImagePreview(URL.createObjectURL(file));
    // Picking an image is choosing image mode.
    setTheme((prev) => ({ ...prev, card_bg_type: "image" }));
  };

  /** Drop a picked file (back to the saved image), or else the saved image itself. */
  const handleClearCardImage = () => {
    if (cardImageFile) {
      if (cardImagePreview.startsWith("blob:")) URL.revokeObjectURL(cardImagePreview);
      setCardImageFile(null);
      setCardImagePreview("");
      return;
    }
    setTheme((prev) => ({ ...prev, card_bg_image: "" }));
  };

  // ==========================================
  // Prize Ladder handlers
  // ==========================================
  const handleAddTier = () => {
    setTiers((prev) => {
      const maxRankTo = prev.reduce((max, t) => {
        const toVal = Number(t.rank_to || t.rank_from || 0);
        return Math.max(max, toVal);
      }, 0);
      const nextFrom = maxRankTo > 0 ? maxRankTo + 1 : 1;
      const nextTo = nextFrom;
      return [
        ...prev,
        {
          _localId: Date.now(),
          rank_from: nextFrom,
          rank_to: nextTo,
          rank_label: `#${nextFrom}`,
          rewards: [],
          sort_order: prev.length + 1,
        },
      ];
    });
  };

  const handleUpdateTier = (index, field, value) => {
    setTiers((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const setTierRewards = (index, rewards) => handleUpdateTier(index, "rewards", rewards);

  const handlePxAmountChange = (index, tier, value) =>
    setTierRewards(index, composeRewards(value, coinRowsOf(tier.rewards)));

  const handleCoinRowChange = (index, tier, coinIdx, patch) =>
    setTierRewards(
      index,
      composeRewards(
        pxAmountOf(tier.rewards),
        coinRowsOf(tier.rewards).map((row, i) => (i === coinIdx ? { ...row, ...patch } : row))
      )
    );

  const handleAddCoinRow = (index, tier) =>
    setTierRewards(
      index,
      composeRewards(pxAmountOf(tier.rewards), [
        ...coinRowsOf(tier.rewards),
        { currency: "USDT", amount: "" },
      ])
    );

  const handleRemoveCoinRow = (index, tier, coinIdx) =>
    setTierRewards(
      index,
      composeRewards(
        pxAmountOf(tier.rewards),
        coinRowsOf(tier.rewards).filter((_, i) => i !== coinIdx)
      )
    );

  const handleRemoveTier = (index) => {
    setTiers((prev) => {
      const target = prev[index];
      if (target.id) {
        setDeletedPrizeIds((d) => [...d, target.id]);
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  // One total per currency: a pool of PX, USDT and BTC has three numbers in it,
  // and adding them together would state something untrue.
  const prizeTotals = totalRewards(tiers);

  // ==========================================
  // Submit & Save Form
  // ==========================================
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    if (!formData.name?.trim()) {
      toast.error("Please enter event name");
      return;
    }
    if (!isEditing && !formData.event_code?.trim()) {
      toast.error("Please enter event code");
      return;
    }
    if (!formData.start_date || !formData.end_date) {
      toast.error("Please provide both start and end dates");
      return;
    }
    if (utcMs(formData.end_date) <= utcMs(formData.start_date)) {
      toast.error("End date must be after start date");
      return;
    }
    const designProblem = themeProblem(theme, { hasImage: hasCardImage, enabled: Boolean(formData.theme_enabled) });
    if (designProblem) {
      toast.error(`App Design: ${designProblem}`);
      return;
    }

    setLoading(true);
    try {
      /**
       * A date is sent only when it differs from the one loaded.
       *
       * The form edits to the minute while the API stores seconds, so echoing
       * back an untouched date submits it up to 59 seconds earlier than it is
       * stored — which a running event refuses. Leaving the field out entirely
       * makes the API skip that check and leave the date alone.
       */
      const dateIfChanged = (current, loaded) =>
        !isEditing || current !== loaded ? fromUtcInputValue(current) : undefined;

      const payload = {
        name: formData.name.trim(),
        description: formData.description?.trim() || "",
        banner_url: formData.banner_url?.trim() || "",
        prize_info_text: formData.prize_info_text?.trim() || "",
        support_url: formData.support_url?.trim() || "",
        sponsor_cta_url: formData.sponsor_cta_url?.trim() || "",
        leaderboard_limit: formData.leaderboard_limit !== "" ? Number(formData.leaderboard_limit) : 10000,
        rank_bucket_size: formData.rank_bucket_size !== "" ? Number(formData.rank_bucket_size) : 100,
        prize_top_n: formData.prize_top_n !== "" ? Number(formData.prize_top_n) : 1000,
        notify_on_create: Boolean(formData.notify_on_create),
        notify_on_running: Boolean(formData.notify_on_running),
        notify_on_close: Boolean(formData.notify_on_close),
        notify_on_winner: Boolean(formData.notify_on_winner),
        // An untouched default is sent as empty, which the API stores as
        // "nothing saved" — so it keeps being worded from the live rates
        // instead of freezing today's numbers into text. Rows without a title
        // are half-typed "+ Add" rows, not content.
        eligibility: eligibilityIsDefault
          ? []
          : eligibility
              .filter((row) => row.title?.trim())
              .map((row, i) => ({
                title: row.title.trim(),
                description: row.description?.trim() || "",
                sort_order: i + 1,
                is_active: row.is_active !== false,
              })),
        // Both sent whole: a blank field clears it back to the app default.
        theme: themePayload(theme),
        theme_enabled: Boolean(formData.theme_enabled),
        rules: rulesPayload(rules),
      };

      const pointRates = Object.fromEntries(
        POINT_RATES.map((r) => [r.field, formData[r.field] !== "" ? Number(formData[r.field]) : r.fallback])
      );

      const endDate = dateIfChanged(formData.end_date, loadedDates.end);
      if (endDate !== undefined) payload.end_date = endDate;

      if (!isEditing) {
        payload.event_code = formData.event_code.trim().toLowerCase().replace(/\s+/g, "_");
        payload.start_date = fromUtcInputValue(formData.start_date);
        payload.daily_check_in_reward = formData.daily_check_in_reward !== "" ? Number(formData.daily_check_in_reward) : 1;
        payload.check_in_credits_coin = Boolean(formData.check_in_credits_coin);
        Object.assign(payload, pointRates);
      } else if (!isRunning && !isCompleted) {
        // A running or completed event refuses these outright, so they are only
        // sent for a draft — and the start date only if it actually moved.
        const startDate = dateIfChanged(formData.start_date, loadedDates.start);
        if (startDate !== undefined) payload.start_date = startDate;
        payload.daily_check_in_reward = formData.daily_check_in_reward !== "" ? Number(formData.daily_check_in_reward) : 1;
        payload.check_in_credits_coin = Boolean(formData.check_in_credits_coin);
      }

      // Point rates stay editable on a running event — a new rate applies to
      // points earned from then on. Only a completed event refuses them.
      if (isEditing && !isCompleted) Object.assign(payload, pointRates);

      let targetEventId = id;

      if (isEditing) {
        await editEvent(id, payload);
      } else {
        const res = await createEvent(payload);
        targetEventId = res?.data?.id || res?.id;
      }

      if (!targetEventId) {
        throw new Error("Could not retrieve event ID");
      }

      // The card image goes up once the event exists. A failure here does not
      // undo the save — it says so, and the event shows the gradient meanwhile.
      if (cardImageFile) {
        try {
          await uploadEventCardImage(targetEventId, cardImageFile);
        } catch (err) {
          toast.error(`Card image not uploaded — ${err.message || "try again from Edit"}`, { duration: 6000 });
        }
      }

      // Handle Deleted Sponsors
      if (isEditing && deletedSponsorIds.length > 0) {
        for (const sId of deletedSponsorIds) {
          try {
            await deleteEventSponsor(targetEventId, sId);
          } catch {}
        }
      }

      // Handle Sponsors creation / update
      //
      // A chosen file has to go as multipart, since that is the only way to
      // carry it; without one the row is plain JSON. The API takes the file
      // under `logo` and treats it as winning over any URL in the same request.
      const failedSponsors = [];

      for (const sp of sponsors) {
        if (!sp.name?.trim()) continue;

        let spPayload;
        let isMultipart = false;

        if (sp.logo_file) {
          spPayload = new FormData();
          spPayload.append("name", sp.name.trim());
          spPayload.append("website_url", sp.website_url?.trim() || "");
          spPayload.append("sort_order", Number(sp.sort_order) || 1);
          spPayload.append("is_active", sp.is_active !== false ? "true" : "false");
          spPayload.append("logo", sp.logo_file);
          isMultipart = true;
        } else {
          spPayload = {
            name: sp.name.trim(),
            website_url: sp.website_url?.trim() || "",
            sort_order: Number(sp.sort_order) || 1,
            is_active: sp.is_active !== false,
          };
          // Only a URL somebody actually edited. Sending back the absolute one
          // the API just handed us would overwrite the relative path it stores.
          if (!sp.id || iconUrlChanged(sp.icon_url, sp.icon_url_original)) {
            spPayload.icon_url = sp.icon_url?.trim() || "";
          }
        }

        try {
          if (sp.id) {
            // Edits to an existing sponsor used to be dropped on the floor
            // here, which made the logo field look broken rather than unsaved.
            await updateEventSponsor(targetEventId, sp.id, spPayload, isMultipart);
          } else {
            await addEventSponsor(targetEventId, spPayload, isMultipart);
          }
        } catch (err) {
          // A logo the API refuses is the likeliest failure, and silence would
          // leave the sponsor looking saved with no picture and no reason why.
          failedSponsors.push(`${sp.name.trim()}: ${err.message || "could not be saved"}`);
        }
      }

      if (failedSponsors.length > 0) {
        toast.error(`Sponsor problems — ${failedSponsors.join("; ")}`, { duration: 6000 });
      }

      // Handle Deleted Prizes
      if (isEditing && deletedPrizeIds.length > 0) {
        for (const pId of deletedPrizeIds) {
          try {
            await deleteEventPrize(targetEventId, pId);
          } catch {}
        }
      }

      // Handle Prizes creation / update
      for (let i = 0; i < tiers.length; i++) {
        const t = tiers[i];
        const rankFrom = Number(t.rank_from) || 1;
        const rankTo = Number(t.rank_to) || rankFrom;

        // `rewards` is the whole prize: the API derives `reward_px`,
        // `reward_usdt` and the display text from it. The one exception is a
        // tier with nothing in it — `prize_text` is NOT NULL on the API side
        // and cannot be derived from an empty list, so it has to say so.
        const { rewards } = buildRewards(t.rewards);

        const tierPayload = {
          rank_from: rankFrom,
          rank_to: rankTo,
          rank_label: t.rank_label || `#${rankFrom}${rankTo > rankFrom ? ` - #${rankTo}` : ""}`,
          rewards,
          ...(rewards.length === 0 ? { prize_text: EMPTY_PRIZE_TEXT } : {}),
          sort_order: i + 1,
        };

        try {
          if (t.id) {
            await updateEventPrize(targetEventId, t.id, tierPayload);
          } else {
            await createEventPrize(targetEventId, tierPayload);
          }
        } catch {}
      }

      toast.success(isEditing ? "Event updated successfully!" : "Event created successfully!");
      // Automatically navigate to readable overview page
      navigate(`/event/${targetEventId}`);
    } catch (err) {
      toast.error(err.message || "Failed to save event");
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="flex h-screen bg-gray-50/50">
        <Sidebar />
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          <Navbar />
          <div className="flex-1 flex items-center justify-center">
            <div className="flex flex-col items-center gap-3">
              <FiRefreshCw className="animate-spin text-blue-600" size={24} />
              <p className="text-xs text-gray-500 font-medium">Loading event details...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50/50">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <Navbar />

        <main className="flex-1 overflow-y-auto px-4 sm:px-8 py-6">
          <div className="max-w-5xl mx-auto space-y-6">
            {/* Top Bar Navigation & Actions */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => navigate(isEditing ? `/event/${id}` : "/event")}
                  className="p-2.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-600 rounded-xl transition shadow-sm"
                  title="Back"
                >
                  <FiArrowLeft size={16} />
                </button>
                <div>
                  <h1 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                    <FiGift className="text-blue-600" /> {isEditing ? `Edit Event: ${formData.name || "Event"}` : "Create New Event"}
                  </h1>
                  <p className="text-xs text-gray-400 mt-0.5">
                    Configure dates, sponsors, prize ladder, and leaderboard rules all in one place
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 flex-wrap">
                <UtcClock />
                <button
                  type="button"
                  onClick={() => navigate(isEditing ? `/event/${id}` : "/event")}
                  className="px-4 py-2.5 text-xs font-medium text-gray-600 hover:bg-gray-100 bg-white border border-gray-200 rounded-xl transition"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={loading}
                  onClick={handleSubmit}
                  className="px-5 py-2.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-xl shadow-sm shadow-blue-600/20 transition flex items-center gap-2"
                >
                  {loading ? (
                    <FiRefreshCw size={15} className="animate-spin" />
                  ) : (
                    <FiSave size={15} />
                  )}
                  {isEditing ? "Save Changes" : "Save Event"}
                </button>
              </div>
            </div>

            {isComingSoon && (
              <div className="p-4 bg-violet-50 border border-violet-200 rounded-2xl text-xs text-violet-800 flex items-start gap-2.5">
                <FiInfo className="mt-0.5 shrink-0 text-violet-600" size={16} />
                <span>
                  This event is <strong>Coming Soon</strong> — users can see it and its start date, but
                  check-in and scoring stay off until it opens. Everything here is still editable, and
                  it goes live on its own at the start date.
                </span>
              </div>
            )}

            {isRunning && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-2xl text-xs text-amber-800 flex items-start gap-2.5">
                <FiInfo className="mt-0.5 shrink-0 text-amber-600" size={16} />
                <span>
                  This event is currently <strong>Running</strong>. Start date, daily rewards, and event
                  code are locked to preserve ongoing participant data integrity. The end date can still
                  be moved either way.
                </span>
              </div>
            )}

            {/* Progress bar + step rail */}
            <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm space-y-4">
              <div className="flex items-end justify-between gap-3">
                <div>
                  <p className="text-xs font-bold text-gray-800">
                    Step {step + 1} of {STEPS.length} · {STEPS[step].label}
                  </p>
                  <p className="text-[11px] text-gray-400">{STEPS[step].hint}</p>
                </div>
                <span className="text-[11px] font-semibold text-blue-600 shrink-0">
                  {progressPct}% complete
                </span>
              </div>

              <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-600 rounded-full transition-all duration-300"
                  style={{ width: `${progressPct}%` }}
                />
              </div>

              <div className="flex items-stretch gap-1 overflow-x-auto pb-1">
                {STEPS.map((s, i) => {
                  const isCurrent = i === step;
                  // "Done" means the step's own rule passes, not merely that it
                  // was walked past — an optional step counts as done at once.
                  const isDone = !stepProblem(i);

                  return (
                    <button
                      key={s.key}
                      type="button"
                      onClick={() => goToStep(i)}
                      className={`flex-1 min-w-[104px] flex items-center gap-2 px-2.5 py-2 rounded-xl border text-left transition ${
                        isCurrent
                          ? "border-blue-500 bg-blue-50/70 ring-2 ring-blue-500/10"
                          : "border-gray-200 bg-white hover:border-gray-300"
                      }`}
                    >
                      <span
                        className={`w-6 h-6 shrink-0 rounded-full flex items-center justify-center text-[11px] font-bold ${
                          isCurrent
                            ? "bg-blue-600 text-white"
                            : isDone
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-gray-100 text-gray-400"
                        }`}
                      >
                        {isDone && !isCurrent ? <FiCheck size={12} /> : i + 1}
                      </span>
                      <span
                        className={`text-[11px] font-semibold truncate ${
                          isCurrent ? "text-blue-800" : isDone ? "text-gray-700" : "text-gray-400"
                        }`}
                      >
                        {s.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Main Form Box */}
            {/* Never self-submits: every save is an explicit button press.
                A six-step form with forty fields is not somewhere Enter should
                be able to create the event. */}
            <form onSubmit={(e) => e.preventDefault()} className="space-y-6">
              {/* Section 1: Timeline & Dates */}
              <div className={`bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-4 ${step === 0 ? "" : "hidden"}`}>
                <h3 className="text-xs font-bold text-gray-800 uppercase tracking-wider flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-blue-600"></span> 1. Timeline & Schedule
                </h3>

                {/* The clock the dates below are set against, with the
                    shortcuts that are only meaningful relative to it. */}
                <UtcClock
                  variant="banner"
                  onStartNow={isRunning || isCompleted ? undefined : handleStartNow}
                  onAddDays={isCompleted ? undefined : handleEndAfterDays}
                  disabled={loading}
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1 flex items-center gap-1.5">
                      Start Date &amp; Time <span className="text-red-500">*</span>
                      <span className="px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded text-[9px] font-bold uppercase tracking-wider">
                        UTC
                      </span>
                    </label>
                    <input
                      type="datetime-local"
                      value={formData.start_date}
                      onChange={(e) => setFormData((prev) => ({ ...prev, start_date: e.target.value }))}
                      disabled={isRunning || isCompleted}
                      className={`w-full px-3 py-2.5 border rounded-xl text-xs font-mono transition focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
                        isRunning || isCompleted
                          ? "bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed"
                          : "border-gray-200 bg-white text-gray-800"
                      }`}
                    />
                    <p className="text-[10px] text-gray-400 mt-1">
                      Pick event start in UTC timezone
                    </p>
                    {/* The same instant on the reader's own clock, so the offset
                        is visible rather than something to work out. */}
                    {localHintFor(formData.start_date) && (
                      <p className="text-[10px] text-gray-400">
                        Your local time:{" "}
                        <span className="font-mono text-gray-500">{localHintFor(formData.start_date)}</span>
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1 flex items-center gap-1.5">
                      End Date &amp; Time <span className="text-red-500">*</span>
                      <span className="px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded text-[9px] font-bold uppercase tracking-wider">
                        UTC
                      </span>
                    </label>
                    <input
                      type="datetime-local"
                      value={formData.end_date}
                      onChange={(e) => setFormData((prev) => ({ ...prev, end_date: e.target.value }))}
                      disabled={isCompleted}
                      className={`w-full px-3 py-2.5 border rounded-xl text-xs font-mono transition focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
                        isCompleted
                          ? "bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed"
                          : "border-gray-200 bg-white text-gray-800"
                      }`}
                    />
                    <p className="text-[10px] text-gray-400 mt-1">
                      Pick event end in UTC timezone
                    </p>
                    {/* The same instant on the reader's own clock, so the offset
                        is visible rather than something to work out. */}
                    {localHintFor(formData.end_date) && (
                      <p className="text-[10px] text-gray-400">
                        Your local time:{" "}
                        <span className="font-mono text-gray-500">{localHintFor(formData.end_date)}</span>
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Section 2: Basic Details */}
              <div className={`bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-4 ${step === 1 ? "" : "hidden"}`}>
                <h3 className="text-xs font-bold text-gray-800 uppercase tracking-wider flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-blue-600"></span> 2. Basic Information
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Event Code <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="event_code"
                      disabled={isEditing}
                      placeholder="e.g. summer_mining_2026"
                      value={formData.event_code}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded-xl text-xs transition focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
                        isEditing ? "bg-gray-50 text-gray-500 border-gray-200 cursor-not-allowed" : "border-gray-200 bg-white"
                      }`}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Event Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      placeholder="e.g. Summer Mining Bonanza 2026"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-200 bg-white rounded-xl text-xs transition focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
                    <textarea
                      name="description"
                      rows={3}
                      placeholder="Describe the event goals, rules, and rewards..."
                      value={formData.description}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-200 bg-white rounded-xl text-xs transition focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    />
                  </div>
                </div>

                <div className="pt-1 space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                        Eligible Point Activities ({eligibility.length})
                      </p>
                      <p className="text-[11px] text-gray-400">
                        Shown in the app in this order — use the arrows to reorder
                        {eligibilityIsDefault && " · default list, follows the point rates until you edit it"}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={handleUseDefaultEligibility}
                        className="px-3.5 py-1.5 bg-white hover:bg-gray-50 text-gray-600 text-xs font-semibold rounded-xl transition border border-gray-200 flex items-center gap-1.5"
                        title="Fill in the standard list, using this event's point rates"
                      >
                        <FiRefreshCw size={13} /> Use defaults
                      </button>
                      <button
                        type="button"
                        onClick={handleAddEligibility}
                        className="px-3.5 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-semibold rounded-xl transition border border-blue-200 flex items-center gap-1.5"
                      >
                        <FiPlus size={14} /> Add
                      </button>
                    </div>
                  </div>

                  {eligibility.length === 0 ? (
                    <div className="p-4 border-2 border-dashed border-gray-200 rounded-2xl text-center text-xs text-gray-400">
                      Empty — the app will show the default list. To show nothing, keep the rows
                      and hide them with the eye icon instead. Click &quot;+ Add&quot; to add a row.
                    </div>
                  ) : (
                    eligibility.map((row, idx) => (
                      <div
                        key={row._localId || idx}
                        className={`p-3 rounded-2xl border flex items-start gap-3 ${
                          row.is_active === false ? "bg-gray-50/50 border-dashed border-gray-200 opacity-60" : "bg-gray-50 border-gray-200"
                        }`}
                      >
                        <div className="flex flex-col items-center gap-1 pt-1">
                          <button
                            type="button"
                            onClick={() => handleMoveEligibility(idx, -1)}
                            disabled={idx === 0}
                            className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition disabled:opacity-30"
                            title="Move up"
                          >
                            <FiArrowUp size={13} />
                          </button>
                          <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-[11px] font-bold">
                            {idx + 1}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleMoveEligibility(idx, 1)}
                            disabled={idx === eligibility.length - 1}
                            className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition disabled:opacity-30"
                            title="Move down"
                          >
                            <FiArrowDown size={13} />
                          </button>
                        </div>

                        <div className="flex-1 min-w-0 space-y-2">
                          <input
                            type="text"
                            placeholder="Title, e.g. KYC Verified"
                            maxLength={191}
                            value={row.title}
                            onChange={(e) => handleUpdateEligibility(idx, "title", e.target.value)}
                            className="w-full px-2.5 py-1.5 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-gray-800 focus:ring-2 focus:ring-blue-500/20"
                          />
                          <textarea
                            rows={2}
                            placeholder="Description, e.g. Only KYC-verified accounts can win a prize."
                            value={row.description}
                            onChange={(e) => handleUpdateEligibility(idx, "description", e.target.value)}
                            className="w-full px-2.5 py-1.5 bg-white border border-gray-200 rounded-xl text-xs text-gray-800 focus:ring-2 focus:ring-blue-500/20"
                          />
                        </div>

                        <div className="flex flex-col items-center gap-1">
                          {/* Hidden rows stay saved, so an activity can be switched
                              off for a while without retyping it later. */}
                          <button
                            type="button"
                            onClick={() => handleUpdateEligibility(idx, "is_active", row.is_active === false)}
                            className={`p-2 rounded-xl transition ${
                              row.is_active === false
                                ? "text-gray-400 hover:text-emerald-600 hover:bg-emerald-50"
                                : "text-emerald-600 hover:text-gray-500 hover:bg-gray-100"
                            }`}
                            title={row.is_active === false ? "Hidden in the app — click to show" : "Shown in the app — click to hide"}
                          >
                            {row.is_active === false ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRemoveEligibility(idx)}
                            className="p-2 text-gray-400 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition"
                            title="Remove"
                          >
                            <FiTrash2 size={16} />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                <div className="pt-1 space-y-2">
                  <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                    Notifications
                  </p>

                  {NOTIFY_MOMENTS.map((moment) => {
                    const on = Boolean(formData[moment.field]);
                    const sentAt = loadedNotifyStamps[moment.field];

                    return (
                      <label
                        key={moment.field}
                        className={`flex items-center justify-between gap-3 p-3 rounded-2xl border cursor-pointer transition ${
                          on ? "bg-blue-50/60 border-blue-200" : "bg-gray-50 border-gray-200"
                        }`}
                      >
                        <span className="min-w-0 flex-1">
                          <span className="block text-xs font-semibold text-gray-800">
                            {moment.label}
                          </span>
                          <span className="block text-[11px] text-gray-500 mt-0.5">
                            {sentAt
                              ? `Already sent on ${toUtcInputValue(sentAt).replace("T", " ")} UTC — it will not be sent again.`
                              : `${moment.when} · ${moment.sends}`}
                          </span>
                        </span>
                        <span className="relative flex-shrink-0">
                          <input
                            type="checkbox"
                            name={moment.field}
                            checked={on}
                            onChange={handleChange}
                            className="peer sr-only"
                          />
                          <span className="block w-10 h-6 rounded-full bg-gray-300 peer-checked:bg-blue-600 transition-colors" />
                          <span className="absolute left-1 top-1 w-4 h-4 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-4" />
                        </span>
                      </label>
                    );
                  })}

                  <p className="text-[10px] text-gray-400">
                    Nothing is sent while the event is a draft, and a message that has already gone out
                    is never repeated.
                  </p>
                </div>
              </div>

              {/* Section 3: Event Sponsors */}
              <div className={`bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-4 ${step === 2 ? "" : "hidden"}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xs font-bold text-gray-800 uppercase tracking-wider flex items-center gap-2">
                      <FiShield className="text-blue-600" /> 3. Event Sponsors ({sponsors.length})
                    </h3>
                    <p className="text-[11px] text-gray-400">
                      Add logos and partner links to display in the event header & sponsor carousel
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={handleAddSponsor}
                    className="px-3.5 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-semibold rounded-xl transition border border-blue-200 flex items-center gap-1.5"
                  >
                    <FiPlus size={14} /> Add Sponsor
                  </button>
                </div>

                {sponsors.length === 0 ? (
                  <div className="p-6 border-2 border-dashed border-gray-200 rounded-2xl text-center text-xs text-gray-400">
                    No sponsors added yet. Click &quot;+ Add Sponsor&quot; to include event partners.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {sponsors.map((sp, idx) => (
                      <div
                        key={sp.id || sp._localId || idx}
                        className="p-4 bg-gray-50 rounded-2xl border border-gray-200 transition space-y-3"
                      >
                        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-start">
                          <div>
                            <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                              Sponsor Name <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              placeholder="e.g. Zeta Labs"
                              value={sp.name}
                              onChange={(e) => handleUpdateSponsor(idx, "name", e.target.value)}
                              className="w-full px-2.5 py-1.5 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-gray-800 focus:ring-2 focus:ring-blue-500/20"
                            />
                          </div>

                          <div>
                            <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                              Website URL
                            </label>
                            <input
                              type="url"
                              placeholder="https://example.com"
                              value={sp.website_url}
                              onChange={(e) => handleUpdateSponsor(idx, "website_url", e.target.value)}
                              className="w-full px-2.5 py-1.5 bg-white border border-gray-200 rounded-xl text-xs font-medium text-gray-800 focus:ring-2 focus:ring-blue-500/20"
                            />
                          </div>

                          <div>
                            <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                              Logo
                            </label>

                            {/* Focusable so a paste lands on this sponsor and
                                not on whichever row happened to be last — with
                                several rows on screen, a page-wide listener
                                could not tell which one was meant. */}
                            <div
                              tabIndex={0}
                              onPaste={(e) => handleSponsorLogoPaste(idx, e)}
                              className="flex items-center gap-2 rounded-xl outline-none focus-within:ring-2 focus-within:ring-blue-500/20"
                            >
                              {/* Preview: the chosen file, or whatever the row already points at */}
                              {sp.logo_preview ? (
                                <img
                                  src={sp.logo_preview}
                                  alt=""
                                  className="w-9 h-9 shrink-0 object-contain rounded-lg bg-white border border-gray-200 p-1"
                                  onError={(e) => {
                                    e.target.style.visibility = "hidden";
                                  }}
                                />
                              ) : (
                                <div className="w-9 h-9 shrink-0 rounded-lg bg-white border border-dashed border-gray-200 flex items-center justify-center text-gray-300">
                                  <FiImage size={14} />
                                </div>
                              )}

                              <div className="min-w-0 flex-1">
                                <label
                                  className="flex items-center justify-center gap-1.5 px-2 py-1.5 bg-white border border-gray-200 hover:border-blue-400 hover:text-blue-600 rounded-xl text-xs font-semibold text-gray-600 cursor-pointer transition"
                                  title={LOGO_HINT}
                                >
                                  <FiUpload size={13} />
                                  <span className="truncate">
                                    {sp.logo_file ? sp.logo_file.name : "Upload logo"}
                                  </span>
                                  <input
                                    type="file"
                                    accept={LOGO_ACCEPT}
                                    className="hidden"
                                    onChange={(e) =>
                                      handleSponsorLogoChange(idx, e.target.files[0], e.target)
                                    }
                                  />
                                </label>

                                {sp.logo_file ? (
                                  <button
                                    type="button"
                                    onClick={() => handleClearSponsorLogo(idx)}
                                    className="mt-1 text-[10px] font-semibold text-gray-400 hover:text-rose-600 transition"
                                  >
                                    Remove file
                                  </button>
                                ) : (
                                  <input
                                    type="url"
                                    placeholder="…or paste a URL / image"
                                    value={sp.icon_url}
                                    onChange={(e) => {
                                      handleUpdateSponsor(idx, "icon_url", e.target.value);
                                      handleUpdateSponsor(idx, "logo_preview", e.target.value);
                                    }}
                                    // An image on the clipboard is uploaded; a
                                    // pasted URL still pastes as text.
                                    onPaste={(e) => handleSponsorLogoPaste(idx, e)}
                                    className="w-full mt-1 px-2.5 py-1 bg-white border border-gray-200 rounded-xl text-[11px] font-medium text-gray-800 focus:ring-2 focus:ring-blue-500/20"
                                  />
                                )}
                              </div>
                            </div>

                            <p className="text-[10px] text-gray-400 mt-1">
                              Upload, paste a URL, or copy an image and press{" "}
                              <kbd className="px-1 py-0.5 bg-gray-100 border border-gray-200 rounded text-[9px] font-semibold">
                                Ctrl+V
                              </kbd>{" "}
                              here
                            </p>
                          </div>

                          <div className="flex items-start justify-between gap-2 pt-2 sm:pt-0">
                            <div>
                              <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                                Sort Order
                              </label>
                              <input
                                type="number"
                                min="0"
                                value={sp.sort_order}
                                onChange={(e) => handleUpdateSponsor(idx, "sort_order", e.target.value)}
                                className="w-16 px-2 py-1.5 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-gray-800 text-center focus:ring-2 focus:ring-blue-500/20"
                              />
                            </div>

                            <button
                              type="button"
                              onClick={() => handleRemoveSponsor(idx)}
                              className="p-2 text-gray-400 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition mt-4"
                              title="Remove Sponsor"
                            >
                              <FiTrash2 size={16} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Section 4: Prize Ladder & Reward Tiers */}
              <div className={`bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-4 ${step === 3 ? "" : "hidden"}`}>
                <div>
                  <h3 className="text-xs font-bold text-gray-800 uppercase tracking-wider flex items-center gap-2">
                    <FiAward className="text-blue-600" /> 4. Prize Pool Tiers ({tiers.length})
                  </h3>
                  <p className="text-[11px] text-gray-400">
                    Configure PX token rewards and custom coin rewards (e.g. USDT, ETH, SOL)
                  </p>
                </div>

                {tiers.length === 0 ? (
                  <div className="p-6 border-2 border-dashed border-gray-200 rounded-2xl text-center text-xs text-gray-400">
                    No prize tiers configured. Click &quot;+ Add Tier&quot; below to add rank rewards.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {tiers.map((tier, idx) => (
                      <div
                        key={tier.id || tier._localId || idx}
                        className="p-4 bg-gray-50 rounded-2xl border border-gray-200 transition space-y-2"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                          {/* RANK RANGE */}
                          <div className="w-full sm:w-40">
                            <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                              Rank Range
                            </label>
                            <div className="flex items-center gap-1.5">
                              <input
                                type="number"
                                min="1"
                                value={tier.rank_from}
                                onChange={(e) => handleUpdateTier(idx, "rank_from", e.target.value)}
                                className="w-full px-2.5 py-1.5 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-gray-800 focus:ring-2 focus:ring-blue-500/20"
                                placeholder="1"
                              />
                              <span className="text-gray-400 text-xs">-</span>
                              <input
                                type="number"
                                min="1"
                                value={tier.rank_to}
                                onChange={(e) => handleUpdateTier(idx, "rank_to", e.target.value)}
                                className="w-full px-2.5 py-1.5 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-gray-800 focus:ring-2 focus:ring-blue-500/20"
                                placeholder="1"
                              />
                            </div>
                          </div>

                          {/* PX REWARD */}
                          <div className="w-full sm:w-44">
                            <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                              PX Reward
                            </label>
                            <div className="relative">
                              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[10px] font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                                PX
                              </span>
                              <input
                                type="number"
                                min="0"
                                step="any"
                                value={pxAmountOf(tier.rewards)}
                                onChange={(e) => handlePxAmountChange(idx, tier, e.target.value)}
                                className="w-full pl-11 pr-2.5 py-1.5 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-gray-800 focus:ring-2 focus:ring-blue-500/20"
                                placeholder="0"
                              />
                            </div>
                          </div>

                          {/* OTHER COIN REWARDS — one row per currency */}
                          <div className="flex-1">
                            <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                              Other Coin Rewards
                            </label>

                            <div className="space-y-2">
                              {coinRowsOf(tier.rewards).length === 0 ? (
                                <p className="text-[11px] text-gray-400 italic py-1.5">
                                  Only PX token reward configured for this rank tier.
                                </p>
                              ) : (
                                coinRowsOf(tier.rewards).map((row, coinIdx) => (
                                  <div key={coinIdx} className="flex items-center gap-2">
                                    <CurrencySelect
                                      value={row.currency}
                                      onChange={(currency) =>
                                        handleCoinRowChange(idx, tier, coinIdx, { currency })
                                      }
                                      options={COIN_CURRENCIES}
                                      selectClassName="w-24 px-2 py-1.5 bg-emerald-50 border border-emerald-200 rounded-xl text-xs font-bold text-emerald-800 text-center uppercase tracking-wider cursor-pointer focus:bg-white focus:ring-2 focus:ring-emerald-500/20"
                                      inputClassName="w-20 px-2 py-1.5 bg-white border border-emerald-200 rounded-xl text-xs font-bold text-emerald-800 text-center uppercase tracking-wider focus:ring-2 focus:ring-emerald-500/20"
                                    />

                                    <input
                                      type="number"
                                      min="0"
                                      step="any"
                                      value={row.amount ?? ""}
                                      onChange={(e) =>
                                        handleCoinRowChange(idx, tier, coinIdx, { amount: e.target.value })
                                      }
                                      className="flex-1 px-3 py-1.5 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-gray-800 focus:ring-2 focus:ring-blue-500/20"
                                      placeholder="0"
                                    />

                                    <button
                                      type="button"
                                      onClick={() => handleRemoveCoinRow(idx, tier, coinIdx)}
                                      className="p-1.5 text-gray-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition"
                                      title="Remove this coin"
                                    >
                                      <FiX size={14} />
                                    </button>
                                  </div>
                                ))
                              )}

                              <button
                                type="button"
                                onClick={() => handleAddCoinRow(idx, tier)}
                                className="text-[11px] font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1 transition"
                              >
                                <FiPlus size={12} /> Add coin
                              </button>
                            </div>
                          </div>

                          {/* Delete Trash button */}
                          <button
                            type="button"
                            onClick={() => handleRemoveTier(idx)}
                            className="p-2 text-gray-400 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition self-end sm:self-center"
                            title="Remove Tier"
                          >
                            <FiTrash2 size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add Tier Button (Placed between tier cards and prize pool total reward) */}
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={handleAddTier}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-xl transition shadow-sm shadow-blue-600/20 flex items-center gap-1.5"
                  >
                    <FiPlus size={15} /> Add Tier
                  </button>
                </div>

                {/* Total Rewards Summary Box */}
                {tiers.length > 0 && (
                  <div className="p-3.5 bg-blue-50/70 border border-blue-100 rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs">
                    <span className="font-semibold text-blue-900 flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-blue-600"></span> Prize Pool Total Rewards:
                    </span>
                    <div className="flex items-center gap-2 flex-wrap">
                      {prizeTotals.length === 0 ? (
                        <span className="px-2.5 py-1 bg-white rounded-lg text-gray-500 font-bold border border-blue-200">
                          No rewards set yet
                        </span>
                      ) : (
                        prizeTotals.map(({ currency, amount }) => (
                          <span
                            key={currency}
                            className={`px-2.5 py-1 bg-white rounded-lg font-bold border border-blue-200 ${
                              isAutoCredited(currency) ? "text-blue-700" : "text-emerald-700"
                            }`}
                            title={
                              isAutoCredited(currency)
                                ? "Credited automatically at finalize"
                                : "Transferred by hand after finalize"
                            }
                          >
                            +{amount.toLocaleString()} {currency}
                          </span>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Section 5: Check-in & Leaderboard Rules */}
              <div className={`bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-4 ${step === 4 ? "" : "hidden"}`}>
                <h3 className="text-xs font-bold text-gray-800 uppercase tracking-wider flex items-center gap-2">
                  <FiAward className="text-blue-600" /> 5. Points & Leaderboard Rules
                </h3>

                <div className="space-y-2">
                  <div>
                    <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                      Event Points per Activity
                    </p>
                    <p className="text-[11px] text-gray-400">
                      How much each activity is worth on this event&apos;s leaderboard. 0 turns a source off.
                      {isRunning && " Changes apply to points earned from now on — points already earned stay as they are."}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {POINT_RATES.map((r) => (
                      <div key={r.field} className="p-3 bg-gray-50 rounded-2xl border border-gray-200">
                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">{r.label}</label>
                        <div className="flex items-center gap-2">
                          <span className="text-[11px] text-gray-500 shrink-0">1 {r.unit} =</span>
                          <input
                            type="number"
                            name={r.field}
                            min={0}
                            step="any"
                            placeholder={String(r.fallback)}
                            disabled={isCompleted}
                            value={formData[r.field]}
                            onChange={handleChange}
                            className={`w-24 px-2.5 py-1.5 border rounded-xl text-xs font-semibold transition focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
                              isCompleted
                                ? "bg-gray-100 text-gray-500 border-gray-200 cursor-not-allowed"
                                : "border-gray-200 bg-white text-gray-800"
                            }`}
                          />
                          <span className="text-[11px] text-gray-500 shrink-0">pts</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Daily Check-in Reward (Pts)</label>
                    <input
                      type="number"
                      name="daily_check_in_reward"
                      min={0}
                      step="any"
                      placeholder="1"
                      disabled={isRunning || isCompleted}
                      value={formData.daily_check_in_reward}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded-xl text-xs transition focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
                        isRunning || isCompleted ? "bg-gray-50 text-gray-500 border-gray-200 cursor-not-allowed" : "border-gray-200 bg-white"
                      }`}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Prize Top N Winners</label>
                    <input
                      type="number"
                      name="prize_top_n"
                      min={1}
                      placeholder="1000"
                      value={formData.prize_top_n}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-200 bg-white rounded-xl text-xs transition focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    />
                  </div>

                  <div className="sm:col-span-2 flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
                    <input
                      type="checkbox"
                      id="check_in_credits_coin"
                      name="check_in_credits_coin"
                      disabled={isRunning || isCompleted}
                      checked={formData.check_in_credits_coin}
                      onChange={handleChange}
                      className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <label htmlFor="check_in_credits_coin" className="text-xs text-gray-700 font-medium cursor-pointer">
                      Credit actual coins directly to user balance on daily check-in
                    </label>
                  </div>
                </div>
              </div>

              {/* Section 6: Links & Banner */}
              <div className={`bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-4 ${step === 5 ? "" : "hidden"}`}>
                <h3 className="text-xs font-bold text-gray-800 uppercase tracking-wider flex items-center gap-2">
                  <FiLink className="text-blue-600" /> 6. Links, Banner & Payout Instructions
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Banner Image URL</label>
                    <input
                      type="url"
                      name="banner_url"
                      placeholder="https://example.com/banner.png"
                      value={formData.banner_url}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-200 bg-white rounded-xl text-xs transition focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Support URL</label>
                    <input
                      type="url"
                      name="support_url"
                      placeholder="https://t.me/support"
                      value={formData.support_url}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-200 bg-white rounded-xl text-xs transition focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Sponsor CTA URL</label>
                    <input
                      type="url"
                      name="sponsor_cta_url"
                      placeholder="https://example.com/sponsor"
                      value={formData.sponsor_cta_url}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-200 bg-white rounded-xl text-xs transition focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Prize Info / Payout Instructions</label>
                    <textarea
                      name="prize_info_text"
                      rows={2}
                      placeholder="e.g. Top 100 winners will receive USDT prizes into their registered wallets after event finalization..."
                      value={formData.prize_info_text}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-200 bg-white rounded-xl text-xs transition focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    />
                  </div>
                </div>
              </div>

              {/* Section 7: App Design — the Live Event card and screen */}
              <div className={`bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-4 ${step === 6 ? "" : "hidden"}`}>
                <div>
                  <h3 className="text-xs font-bold text-gray-800 uppercase tracking-wider flex items-center gap-2">
                    <FiImage className="text-blue-600" /> 7. App Design
                  </h3>
                  <p className="text-[11px] text-gray-400">
                    How the Live Event card and screen look in the app — only once switched on. Everything
                    is optional: anything left blank is sent as the standard design. Can be changed, and
                    switched on or off, while the event is running.
                  </p>
                </div>

                <EventThemeStep
                  enabled={Boolean(formData.theme_enabled)}
                  onEnabledChange={(on) => setFormData((prev) => ({ ...prev, theme_enabled: on }))}
                  theme={theme}
                  onThemeChange={setTheme}
                  rules={rules}
                  onRulesChange={setRules}
                  // What the API sends the app while no rules are written.
                  defaultRules={defaultRules()}
                  eventName={formData.name}
                  cardImageFile={cardImageFile}
                  cardImagePreview={cardImagePreview}
                  onPickCardImage={handlePickCardImage}
                  onClearCardImage={handleClearCardImage}
                />
              </div>

              {/* Bottom Action Footer — step navigation */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => (step === 0 ? navigate(isEditing ? `/event/${id}` : "/event") : goToStep(step - 1))}
                  disabled={loading}
                  className="px-4 py-2.5 text-xs font-medium text-gray-600 hover:bg-gray-100 bg-white border border-gray-200 rounded-xl transition flex items-center justify-center gap-1.5"
                >
                  <FiChevronLeft size={15} />
                  {step === 0 ? "Cancel" : `Back to ${STEPS[step - 1].label}`}
                </button>

                <div className="flex items-center gap-3">
                  {/* Saving from a middle step is still possible — the header
                      keeps a Save button, so a one-field edit does not mean
                      walking the whole form again. */}
                  {isLastStep ? (
                    <button
                      key="submit-step"
                      type="button"
                      onClick={handleSubmit}
                      disabled={loading}
                      className="px-6 py-2.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-xl shadow-sm shadow-blue-600/20 transition flex items-center gap-2"
                    >
                      {loading ? (
                        <FiRefreshCw size={15} className="animate-spin" />
                      ) : (
                        <FiSave size={15} />
                      )}
                      {isEditing ? "Save Changes" : "Create Event"}
                    </button>
                  ) : (
                    <button
                      key="next-step"
                      type="button"
                      onClick={() => goToStep(step + 1)}
                      disabled={loading}
                      className="px-6 py-2.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-xl shadow-sm shadow-blue-600/20 transition flex items-center gap-2"
                    >
                      Next: {STEPS[step + 1].label}
                      <FiChevronRight size={15} />
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </main>
      </div>
    </div>
  );
};

export default EventForm;
